// De Fee menukaart "vanaf 01/01/2026" — ÉÉN bron voor alle kostcomponenten.
//
// Drie plekken tonen of gebruiken deze cijfers, en ze mogen nooit uit elkaar lopen:
//   1. de Revenue-berekening zelf (RevenuePage in App.jsx: SUPPLIER_COST_EL, BRP,
//      onbalans, groenkost, gas),
//   2. het Menukaart-paneel (revenueDocs.js: REVENUE_CONSTANTS, FEE_EL_PRODUCTEN,
//      FEE_GAS, FEE_VASTRECHT) op de pagina's Revenue én Revenue Calculator,
//   3. het rekenmodel van de Revenue Calculator (revenueCalculator.js: RC_LOCK).
// Een wijziging aan de menukaart gebeurt dus HIER en nergens anders; de drie
// afnemers leiden hun getallen en teksten hiervan af.
//
// Alle bedragen in €/MWh tenzij anders vermeld, excl. btw.

export const MENUKAART_GELDIG_VANAF = '01/01/2026';

export const MENUKAART = {
  el: {
    // Vaste leveranciersmarge op afgenomen (en geïnjecteerde) elektriciteit.
    marge: 4,
    // Sourcing-/balanceringskost (BRP). B2B en Custom (grootverbruik): laag tarief
    // voor alle labels. B2C: laag voor de labels in `laagLabels`, anders hoog.
    // Uitzondering Power2You B2C: per verkoopteam (zie `p2yB2cLaagTeams`). Een
    // NIEUW label of team staat bewust niet in de lijsten → hoog tarief.
    brp: {
      laag: 1,
      hoog: 2.75,   // 25-09-2026: was 2,5; bevestigd 2,75 voor B2C bij de overige/nieuwe labels en teams
      laagLabels: ['Power2You', 'Evident Energie', 'Servolt Energie', 'Smappee Smiles'],
      // Huidige Power2You-verkoopteams (per 02-06-2026) die B2C → laag krijgen.
      // Bewust ZONDER "HOA Team (P2Y)" en "Soltex".
      p2yB2cLaagTeams: ['De Energiespecialist', 'EnergyOnPoint', 'Power2You Team', 'TC Solutions', 'TWAS', 'Team Bert', 'Team Bert Vanuytrecht', 'Vlaamse Volt', 'Wout Energie'],
    },
    // Onbalanskost bij afname. Kleinverbruik: RLP-profiel (Belpex_RLP) vs. SMR3/overige
    // types. Grootverbruik (Custom): 0 bij Belpex/Hourly Belpex met AMR-meting, anders 4.
    onbalansAfname: { rlp: 4, smr3: 2, gvAmr: 0, gvOverig: 4 },
    // Onbalanskost bij injectie (self bills): twee tariefgroepen per energielabel.
    // Groep A = de labels hieronder; elk ander (en elk nieuw) label = groep B.
    onbalansInjectie: {
      groepA: { hoog: 18, laag: 14, labels: ['Smappee Smiles', 'Servolt Energie', 'Power2You', 'Evident Energie'] },
      groepB: { hoog: 20, laag: 15 },
      // Terugvalwaarde voor B2C of zonder segment (komt in self bills niet voor).
      terugval: 20,
    },
    // Garanties van oorsprong bij een groen product (geschat, kan wijzigen).
    groenkost: 0.5,
    // Minimale profileringsfactor in de prijsformule (indexfactor ≥ 1 levert marge op).
    minProfilering: 1,
  },
  gas: {
    marge: 2.5,
    shipper: 1.5,
  },
  // Vastrecht per EAN per jaar.
  vastrecht: {
    basisPerJaar: 30, energiedelenPerJaar: 120,
    // Staffelkorting (contract bijlage 6.6.1): het vastrecht per EAN per MAAND daalt
    // in cumulatieve schijven met het totale aantal actieve EAN's van de partner.
    // Elke schijf wordt aan haar eigen tarief afgerekend (geen terugwerkende
    // korting op de eerste 2.000). `tot` = bovengrens van de schijf (inclusief).
    staffel: [
      { tot: 2000, perMaand: 2.5 },
      { tot: 5000, perMaand: 2.0 },
      { tot: Infinity, perMaand: 1.75 },
    ],
    // Toelaatbaarheidsvoorwaarde (bijlage 6.6.2): de korting geldt alleen zolang het
    // percentage wanbetalers in het eindklantenbestand (rubriek "Statusverdeling
    // vergoeding", gemeten op het moment van de maandelijkse goedkeuringsmail)
    // niet boven dit percentage uitkomt; erboven geldt het standaardtarief tot
    // het percentage weer onder de drempel zakt.
    staffelWanbetalersMaxPct: 3,
  },
  // Referentiemarktprijzen van de menukaart (startwaarden in de Calculator).
  marktprijs: { el: 90, gas: 35 },
};

// Staffelkorting op het vastrecht voor `aantal` actieve EAN's (mag een breuk zijn:
// de calculator rekent met gemiddelde actieve EAN's per jaar). Geeft per jaar:
//   standaard  = aantal × standaardtarief × 12 (wat het zonder staffel zou kosten)
//   kost       = som over de schijven van EAN's in de schijf × schijftarief × 12
//   voordeel   = standaard − kost (0 zolang aantal ≤ de eerste schijf)
//   schijven   = [{ van, tot, perMaand, eans, voordeel }] voor de weergave
export function staffelVoordeel(aantal) {
  const n = Math.max(0, Number(aantal) || 0);
  const schijven = MENUKAART.vastrecht.staffel;
  const standaardPerMaand = schijven[0].perMaand;
  let vorige = 0, kost = 0;
  const uit = schijven.map((s) => {
    const inSchijf = Math.max(0, Math.min(n, s.tot) - vorige);
    const voordeel = inSchijf * (standaardPerMaand - s.perMaand) * 12;
    kost += inSchijf * s.perMaand * 12;
    const rij = { van: vorige + 1, tot: s.tot, perMaand: s.perMaand, eans: inSchijf, voordeel };
    vorige = s.tot;
    return rij;
  });
  const standaard = n * standaardPerMaand * 12;
  return { aantal: n, standaard, kost, voordeel: standaard - kost, schijven: uit, drempel: schijven[0].tot };
}

// Getal → Belgische notatie voor menukaartteksten ("2,5", "0,50").
export const mkGetal = (n, decimalen) => Number(n).toLocaleString('nl-BE', { minimumFractionDigits: decimalen || 0, maximumFractionDigits: decimalen == null ? 2 : decimalen });
export const mkEur = (n, decimalen) => `€ ${mkGetal(n, decimalen)}`;

// BRP-kost B2C per energielabel (zonder de teamuitzondering van Power2You).
export const brpLaagLabel = (label) => MENUKAART.el.brp.laagLabels.includes(label);
export const brpCostForLabel = (label) => (brpLaagLabel(label) ? MENUKAART.el.brp.laag : MENUKAART.el.brp.hoog);

// Volledige BRP-regel: segment + label + (Power2You) verkoopteam. `bronLabel`
// vertaalt een prospectlabel naar het label waarmee gerekend wordt; zonder
// vertaler geldt het label zelf.
export const brpCostVoor = (label, segment, team, bronLabel = (l) => l) => {
  const seg = (segment || '').toUpperCase();
  if (seg.includes('B2B') || seg.includes('CUSTOM')) return MENUKAART.el.brp.laag;
  const bron = bronLabel(label);
  if (bron === 'Power2You') return MENUKAART.el.brp.p2yB2cLaagTeams.includes(team) ? MENUKAART.el.brp.laag : MENUKAART.el.brp.hoog;
  return brpCostForLabel(bron);
};

// Onbalanstarieven injectie (self bills) per energielabel.
export const sbImbalanceRatesForLabel = (label) => {
  const { groepA, groepB } = MENUKAART.el.onbalansInjectie;
  return groepA.labels.includes(label) ? { high: groepA.hoog, low: groepA.laag } : { high: groepB.hoog, low: groepB.laag };
};
